import { Autocomplete, Button, Grid, IconButton, TextField, Typography } from "@mui/material"
import { Box } from "@mui/system"
import Loader from "components/Loader/loader"
import { useEffect, useState } from "react"
import { createKPIConfig, getAlertKpiProgramsSectors, updateKPIConfig } from "service/service"
import FileUploadIcon from '../../assets/kpi-file.svg'
import DeleteIcon from '../../assets/kpi-delete.svg'
import colors from "assets/theme/base/colors"
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
interface KPIConfig {
    _id: string,
    name: string,
    description: string,
    update_frequency_unit: string | null,
    update_frequency_value: number,
    isActive: boolean,
    source_config: any,
    target_config: any,
    program: string | null,
    project: string | null,
    sector: string | null
}

export const KPIForm = ({ onCreateKPI, kpiConfig = {}, onCancel }: any) => {
    const [kpi, setKpi] = useState<KPIConfig>(kpiConfig)
    const [selectedFile, setSelectedFile] = useState<any>(null)
    const [programOptions, setProgramOptions] = useState([]);
    const [sectorOptions, setSectorOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [errorText, setErrorText] = useState<string | null>(null);

    const parseFile = (file: any) => {
        const reader = new FileReader()
        reader.readAsText(file)
        reader.onload = () => {
            const text = reader.result as string
            if (text) {
                const jsonData: any = JSON.parse(text)
                if (jsonData.source_config) {
                    setKpi(kpi => ({ ...kpi, source_config: jsonData.source_config }))
                }
                if (jsonData.target_config) {
                    setKpi(kpi => ({ ...kpi, target_config: jsonData.target_config }))
                }
            }
        }
    }

    const handleFile = (e: any) => {
        setErrorText(null)
        if (e.target?.files?.length) {
            setSelectedFile(e.target.files[0])
            parseFile(e.target?.files?.[0])
        }
    }

    const saveKPIConfig = async () => {
        if (!kpi.program || !kpi.project || !kpi.sector || !kpi.name || !kpi.update_frequency_unit || !kpi.update_frequency_value) {
            setErrorText('Please fill up all required fields.')
            return
        }
        if (!kpi.source_config || !kpi.source_config.type || (!kpi.source_config.queries?.length && !kpi.source_config.query)) {
            return setErrorText('Please upload a valid config file with source type and query')
        }
        setLoading(true)
        const result = kpi._id ? await updateKPIConfig(kpi) : await createKPIConfig(kpi)
        setLoading(false)
        if (result.statusCode === 200) {
            onCreateKPI()
        } else {
            setErrorText(result.data.message)
        }
    }

    const fetchData = async () => {
        setLoading(true);
        const result: any = await getAlertKpiProgramsSectors();
        if (result.data) {
            setProgramOptions(result.data.programs)
            setSectorOptions(result.data.sectors)
        }
        setLoading(false);
    };

    const handleChange = (name: string, value: any) => {
        setErrorText(null)
        setKpi({ ...kpi, [name]: value })
    }

    useEffect(() => {
        fetchData();
    }, []);

    return (
        <Box sx={{
            padding: '28px 20px',
            bgcolor: `${colors.modal.bg} !important`,
            color: `${colors.modal.text} !important`,
            '& .MuiInputLabel-root': {
                fontStyle: 'normal !important',
                '&.MuiInputLabel-shrink': {
                    fontStyle: 'italic !important'
                }
            }
        }}>
            {!!loading && <Loader />}
            <Grid container spacing={2}>
                <Grid item xs={6}>
                    <TextField
                        label="KPI name*"
                        variant="outlined"
                        size="small"
                        value={kpi?.name}
                        onChange={(e) => handleChange('name', e.target.value)}
                        sx={{
                            width: 400,
                            "& .MuiInputBase-input": {
                                color: `${colors.input.text} !important`,
                            },
                            "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: `${colors.input.border} !important`,
                            },
                            "& .MuiInputLabel-root": {
                                color: `${colors.input.text} !important`,
                                fontStyle: 'normal !important',
                                "&.MuiInputLabel-shrink": {
                                    fontStyle: 'italic !important',
                                },
                            },
                            "& .MuiInputLabel-root.Mui-focused": {
                                color: `${colors.input.text} !important`,
                            },
                            "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                borderColor: `${colors.input.borderFocus} !important`,
                            },
                            "& .MuiFormHelperText-root": {
                                color: `${colors.input.text} !important`,
                            },
                        }}
                    />
                </Grid>
                <Grid item xs={3}>
                    <TextField
                        label="Frequency Value*"
                        variant="outlined"
                        size="small"
                        type={'number'}
                        value={kpi?.update_frequency_value}
                        onChange={(e) => handleChange('update_frequency_value', parseInt(e.target.value))}
                        sx={{
                            width: '100%',
                            "& .MuiInputBase-input": {
                                color: `${colors.input.text} !important`,
                            },
                            "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: `${colors.input.border} !important`,
                            },
                            "& .MuiInputLabel-root": {
                                color: `${colors.input.text} !important`,
                                fontStyle: 'normal !important',
                                "&.MuiInputLabel-shrink": {
                                    fontStyle: 'italic !important',
                                },
                            },
                            "& .MuiInputLabel-root.Mui-focused": {
                                color: `${colors.input.text} !important`,
                            },
                            "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                borderColor: `${colors.input.borderFocus} !important`,
                            },
                            "& .MuiFormHelperText-root": {
                                color: `${colors.input.text} !important`,
                            },
                        }}
                    />
                </Grid>
                <Grid item xs={3}>
                    <Autocomplete
                        id="size-small-outlined"
                        size="small"
                        options={['Minutes', 'Hours', 'Days', 'Weeks', 'Months']}
                        value={kpi?.update_frequency_unit}
                        onChange={(e, option) => handleChange('update_frequency_unit', option)}
                        sx={{
                            '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: `${colors.input.border} !important`,
                                border: `1px solid ${colors.input.border}`
                            },
                            '& .MuiAutocomplete-popupIndicator svg': {
                                fill: `${colors.input.text} !important`,
                            },
                            '& .MuiAutocomplete-popupIndicator': {
                                color: `${colors.input.text} !important`,
                                '& svg': {
                                    fill: `${colors.input.text} !important`,
                                },
                            },
                            '& .MuiAutocomplete-clearIndicator': {
                                color: `${colors.input.text} !important`,
                                '& svg': { fill: `${colors.input.text} !important` },
                            },
                        }}
                        componentsProps={{
                            paper: {
                                sx: {
                                    bgcolor: `${colors.dropdown.menuItemBackground} !important`,
                                    color: `${colors.dropdown.menuItemText} !important`,
                                },
                            },
                            popper: {
                                sx: {
                                    '& .MuiAutocomplete-option': {
                                        bgcolor: `${colors.dropdown.menuItemBackground} !important`,
                                        color: `${colors.dropdown.menuItemText} !important`,
                                        '&.Mui-selected': {
                                            backgroundColor: `${colors.dropdown.menuItemSelected} !important`,
                                            color: `${colors.dropdown.menuItemHoverText} !important`,
                                        },
                                        '&.Mui-selected:hover': {
                                            backgroundColor: `${colors.dropdown.menuItemHover} !important`,
                                            color: `${colors.dropdown.menuItemHoverText} !important`,
                                        },
                                        '&:hover': {
                                            backgroundColor: `${colors.dropdown.menuItemHover} !important`,
                                            color: `${colors.dropdown.menuItemHoverText} !important`,
                                        },
                                    },
                                },
                            },
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Unit*"
                                variant="outlined"
                                size="small"
                                sx={{
                                    width: '100%',
                                    "& .MuiInputBase-input": {
                                        color: `${colors.input.text} !important`,
                                    },
                                    "& .MuiOutlinedInput-notchedOutline": {
                                        borderColor: `${colors.input.border} !important`,
                                    },
                                    "& .MuiInputLabel-root": {
                                        color: `${colors.input.text} !important`,
                                        fontStyle: 'normal !important',
                                        "&.MuiInputLabel-shrink": {
                                            fontStyle: 'italic !important',
                                        },
                                    },
                                    "& .MuiInputLabel-root.Mui-focused": {
                                        color: `${colors.input.text} !important`,
                                    },
                                    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                        borderColor: `${colors.input.borderFocus} !important`,
                                    },
                                    "& .MuiFormHelperText-root": {
                                        color: `${colors.input.text} !important`,
                                    },
                                }}
                            />
                        )}
                    />
                </Grid>

            </Grid>
            <Grid mt={1} container spacing={2}>
                <Grid item xs={6}>
                    <Autocomplete
                        id="size-small-outlined"
                        size="small"
                        options={sectorOptions}
                        value={kpi?.sector}
                        onChange={(e, option) => handleChange('sector', option)}
                        sx={{
                            '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: `${colors.input.border} !important`,
                                border: `1px solid ${colors.input.border}`
                            },
                            '& .MuiAutocomplete-popupIndicator svg': {
                                fill: `${colors.input.text} !important`,
                            },
                            '& .MuiAutocomplete-popupIndicator': {
                                color: `${colors.input.text} !important`,
                                '& svg': {
                                    fill: `${colors.input.text} !important`,
                                },
                            },
                            '& .MuiAutocomplete-clearIndicator': {
                                color: `${colors.input.text} !important`,
                                '& svg': { fill: `${colors.input.text} !important` },
                            },
                        }}
                        componentsProps={{
                            paper: {
                                sx: {
                                    bgcolor: `${colors.dropdown.menuItemBackground} !important`,
                                    color: `${colors.dropdown.menuItemText} !important`,
                                },
                            },
                            popper: {
                                sx: {
                                    '& .MuiAutocomplete-option': {
                                        bgcolor: `${colors.dropdown.menuItemBackground} !important`,
                                        color: `${colors.dropdown.menuItemText} !important`,
                                        '&.Mui-selected': {
                                            backgroundColor: `${colors.dropdown.menuItemSelected} !important`,
                                            color: `${colors.dropdown.menuItemHoverText} !important`,
                                        },
                                        '&.Mui-selected:hover': {
                                            backgroundColor: `${colors.dropdown.menuItemHover} !important`,
                                            color: `${colors.dropdown.menuItemHoverText} !important`,
                                        },
                                        '&:hover': {
                                            backgroundColor: `${colors.dropdown.menuItemHover} !important`,
                                            color: `${colors.dropdown.menuItemHoverText} !important`,
                                        },
                                    },
                                },
                            },
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Sector*"
                                variant="outlined"
                                size="small"
                                sx={{
                                    width: 400,
                                    "& .MuiInputBase-input": {
                                        color: `${colors.input.text} !important`,
                                    },
                                    "& .MuiOutlinedInput-notchedOutline": {
                                        borderColor: `${colors.input.border} !important`,
                                    },
                                    "& .MuiInputLabel-root": {
                                        color: `${colors.input.text} !important`,
                                        fontStyle: 'normal !important',
                                        "&.MuiInputLabel-shrink": {
                                            fontStyle: 'italic !important',
                                        },
                                    },
                                    "& .MuiInputLabel-root.Mui-focused": {
                                        color: `${colors.input.text} !important`,
                                    },
                                    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                        borderColor: `${colors.input.borderFocus} !important`,
                                    },
                                    "& .MuiFormHelperText-root": {
                                        color: `${colors.input.text} !important`,
                                    },
                                }}
                            />
                        )}
                    />
                </Grid>
                <Grid item xs={6}>
                    <Autocomplete
                        id="size-small-outlined"
                        size="small"
                        options={programOptions}
                        value={kpi?.program}
                        onChange={(e, option) => handleChange('program', option)}
                        sx={{
                            '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: `${colors.input.border} !important`,
                                border: `1px solid ${colors.input.border}`
                            },
                            '& .MuiAutocomplete-popupIndicator svg': {
                                fill: `${colors.input.text} !important`,
                            },
                            '& .MuiAutocomplete-popupIndicator': {
                                color: `${colors.input.text} !important`,
                                '& svg': {
                                    fill: `${colors.input.text} !important`,
                                },
                            },
                            '& .MuiAutocomplete-clearIndicator': {
                                color: `${colors.input.text} !important`,
                                '& svg': { fill: `${colors.input.text} !important` },
                            },
                        }}
                        componentsProps={{
                            paper: {
                                sx: {
                                    bgcolor: `${colors.dropdown.menuItemBackground} !important`,
                                    color: `${colors.dropdown.menuItemText} !important`,
                                },
                            },
                            popper: {
                                sx: {
                                    '& .MuiAutocomplete-option': {
                                        bgcolor: `${colors.dropdown.menuItemBackground} !important`,
                                        color: `${colors.dropdown.menuItemText} !important`,
                                        '&.Mui-selected': {
                                            backgroundColor: `${colors.dropdown.menuItemSelected} !important`,
                                            color: `${colors.dropdown.menuItemHoverText} !important`,
                                        },
                                        '&.Mui-selected:hover': {
                                            backgroundColor: `${colors.dropdown.menuItemHover} !important`,
                                            color: `${colors.dropdown.menuItemHoverText} !important`,
                                        },
                                        '&:hover': {
                                            backgroundColor: `${colors.dropdown.menuItemHover} !important`,
                                            color: `${colors.dropdown.menuItemHoverText} !important`,
                                        },
                                    },
                                },
                            },
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Program*"
                                variant="outlined"
                                sx={{
                                    width: 400,
                                    "& .MuiInputBase-input": {
                                        color: `${colors.input.text} !important`,
                                    },
                                    "& .MuiOutlinedInput-notchedOutline": {
                                        borderColor: `${colors.input.border} !important`,
                                    },
                                    "& .MuiInputLabel-root": {
                                        color: `${colors.input.text} !important`,
                                        fontStyle: 'normal !important',
                                        "&.MuiInputLabel-shrink": {
                                            fontStyle: 'italic !important',
                                        },
                                    },
                                    "& .MuiInputLabel-root.Mui-focused": {
                                        color: `${colors.input.text} !important`,
                                    },
                                    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                        borderColor: `${colors.input.borderFocus} !important`,
                                    },
                                    "& .MuiFormHelperText-root": {
                                        color: `${colors.input.text} !important`,
                                    },
                                }}
                            />
                        )}
                    />
                </Grid>
            </Grid>
            <Grid mt={1} container spacing={2}>
                <Grid item xs={6}>
                    <TextField
                        label="Project*"
                        variant="outlined"
                        size="small"
                        value={kpi?.project}
                        onChange={(e) => handleChange('project', e.target.value)}
                        sx={{
                            width: 400,
                            "& .MuiInputBase-input": {
                                color: `${colors.input.text} !important`,
                            },
                            "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: `${colors.input.border} !important`,
                            },
                            "& .MuiInputLabel-root": {
                                color: `${colors.input.text} !important`,
                                fontStyle: 'normal !important',
                                "&.MuiInputLabel-shrink": {
                                    fontStyle: 'italic !important',
                                },
                            },
                            "& .MuiInputLabel-root.Mui-focused": {
                                color: `${colors.input.text} !important`,
                            },
                            "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                borderColor: `${colors.input.borderFocus} !important`,
                            },
                            "& .MuiFormHelperText-root": {
                                color: `${colors.input.text} !important`,
                            },
                        }}
                    />
                </Grid>
                <Grid item xs={6}>
                    <TextField
                        label="Description"
                        variant="outlined"
                        size="small"


                        value={kpi?.description}
                        onChange={(e) => handleChange('description', e.target.value)}
                        sx={{
                            width: 400,
                            "& .MuiInputBase-input": {
                                color: `${colors.input.text} !important`,
                            },
                            "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: `${colors.input.border} !important`,
                            },
                            "& .MuiInputLabel-root": {
                                color: `${colors.input.text} !important`,
                                fontStyle: 'normal !important',
                                "&.MuiInputLabel-shrink": {
                                    fontStyle: 'italic !important',
                                },
                            },
                            "& .MuiInputLabel-root.Mui-focused": {
                                color: `${colors.input.text} !important`,
                            },
                            "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                borderColor: `${colors.input.borderFocus} !important`,
                            },
                            "& .MuiFormHelperText-root": {
                                color: `${colors.input.text} !important`,
                            },
                        }}
                    />
                </Grid>
            </Grid>
            <Typography
                style={{
                    color: '#880000',
                    cursor: 'pointer',
                    marginTop: '6px',
                    marginRight: '25px'
                }}
            >
                {errorText ? `*${errorText}` : ''}
            </Typography>
            <div style={{ marginTop: 32, display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                {!!selectedFile ?
                    <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '12px' }}>
                        <img src={FileUploadIcon} alt="File" style={{ width: 18, height: 22, display: 'block' }} />
                        <p style={{ textAlign: 'left', textDecoration: 'underline', color: '#009BD8', margin: 0, fontSize: '14px', fontWeight: 500 }}>{selectedFile?.name}</p>
                        <p style={{ textAlign: 'left', color: '#71758F', fontWeight: 500, fontSize: '14px', margin: 0 }}>{selectedFile?.size ? `${(selectedFile.size / 1024).toFixed(3)} KB` : ''}</p>
                        <IconButton size="small" onClick={() => setSelectedFile(null)} sx={{ p: 0, '&:hover': { background: 'transparent' } }}>
                            <img src={DeleteIcon} alt="Delete" style={{ display: 'block', width: 16, height: 18 }} />
                        </IconButton>
                    </div> :
                    <>
                        <input type='file' accept=".json" style={{ display: 'none' }} id='upload-file' onChange={handleFile} />
                        <label htmlFor="upload-file" style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                            <CloudUploadOutlinedIcon sx={{ color: '#009BD8', fontSize: '24px' }} />
                            <Typography
                                sx={{
                                    color: '#009BD8',
                                    cursor: 'pointer',
                                    fontWeight: 600,
                                    fontSize: '14px'
                                }}
                            >
                                Upload file (.json)
                            </Typography>
                        </label>
                    </>}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: "center", gap: 3 }}>
                    <Typography
                        sx={{
                            color: '#009BD8 !important',
                            cursor: 'pointer',
                            fontWeight: 600,
                            fontSize: '14px',
                            textTransform: 'uppercase'
                        }}
                        onClick={onCancel}
                    >
                        CANCEL
                    </Typography>
                    <Button
                        variant="pepwiseSecondary"
                        onClick={saveKPIConfig}
                        sx={{
                            minWidth: '120px',
                            height: '38px',
                            borderRadius: '4px !important',
                            backgroundColor: '#009BD8 !important',
                            color: '#FFFFFF !important',
                            fontWeight: 600,
                            fontSize: '14px',
                            textTransform: 'uppercase',
                            boxShadow: 'none',
                            '&:hover': {
                                backgroundColor: '#0084b8 !important',
                                boxShadow: 'none'
                            }
                        }}
                    >
                        SAVE
                    </Button>
                </Box>
            </div>

        </Box>
    )
}